import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { select, Store } from '@ngrx/store';

import { isNil, isNull, map as lodashMap } from 'lodash-es';
import { combineLatest, map, Observable, of } from 'rxjs';
import { concatMap, filter } from 'rxjs/operators';

import { AddressEnum } from '../../../../enums/address';
import { MainTabBasePathEnum } from '../../../../enums/main-tabs';
import { ParsedSchool, PropertyProfile } from '../../../../store/models/property-profile.model';
import * as fromMapSelectors from '../../../../store/selectors/map.selectors';
import * as fromPropertyProfileSelectors from '../../../../store/selectors/property-profile.selectors';
import { SchoolMarkerData } from '../../../../typings/profile';
import { MapsService } from '../../../services/maps.service';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'red-profile-schools',
  standalone: false,
  templateUrl: './schools.page.html'
})
export class SchoolsPageComponent implements AfterViewInit {
  private activatedRoute = inject(ActivatedRoute);
  private changeDetectorRef = inject(ChangeDetectorRef);
  private mapsService = inject(MapsService);
  private store = inject(Store);

  profileId: string | null;
  schools$: Observable<ParsedSchool[]>;

  profile: PropertyProfile | null = null;
  defaultBackHref: string | undefined;

  selectedMapTypeId: google.maps.MapTypeId | null = null;
  mapInstance: google.maps.Map | null = null;
  profileLocation$: Observable<google.maps.LatLngLiteral | null>;
  schoolMarkers$: Observable<SchoolMarkerData[]>;
  schoolsBounds: google.maps.LatLngBounds | null = null;
  mapOptions$: Observable<google.maps.MapOptions>;
  markerOptions$: Observable<google.maps.MarkerOptions>;

  constructor() {
    this.defaultBackHref = this.activatedRoute.snapshot.data['defaultBackHref'] ?? MainTabBasePathEnum.PropertySearch;

    this.profileId = this.activatedRoute.snapshot.paramMap.get('id') ?? '';
    this.schoolMarkers$ = of(this.profileId).pipe(
      filter((id) => !isNil(id)),
      concatMap((id) => this.store.pipe(select(fromPropertyProfileSelectors.selectPropertyProfileSchoolMarkers(id)))),
      map((schools) =>
        lodashMap(schools, (school): SchoolMarkerData => ({
          options: {
            icon: {
              labelOrigin: new google.maps.Point(13, 13),
              scaledSize: new google.maps.Size(26, 44),
              url: '/assets/img/pin-standard-red.png'
            },
            label: {
              color: '#ffffff',
              fontSize: '12px',
              fontWeight: 'bold',
              text: `${school.id}`
            }
          },
          position: school.location
        }))
      )
    );

    this.schools$ = of(this.profileId).pipe(
      filter((id) => !isNil(id)),
      concatMap((id) => this.store.pipe(select(fromPropertyProfileSelectors.selectPropertyProfileSchools(id))))
    );

    this.profileLocation$ = this.store.pipe(
      select(fromPropertyProfileSelectors.selectPropertyProfileMapCenter(this.profileId))
    );

    combineLatest([
      this.store.pipe(select(fromPropertyProfileSelectors.selectPropertyProfileSchoolMarkers(this.profileId))),
      this.store.pipe(select(fromPropertyProfileSelectors.selectPropertyProfileMapCenter(this.profileId)))
    ]).subscribe(([schoolMarkers, mapCenter]) => {
      this.schoolsBounds = new google.maps.LatLngBounds();
      this.schoolsBounds.extend(mapCenter);
      for (let i = 0; i < schoolMarkers.length; i++) {
        this.schoolsBounds.extend(schoolMarkers[i].location);
      }
      this.centerOnBounds();
    });

    this.mapOptions$ = this.store.pipe(select(fromMapSelectors.selectOptions));
    this.markerOptions$ = this.store.pipe(
      select(fromMapSelectors.selectMarkerOptions),
      map((markerOptions) => ({
        ...markerOptions,
        icon: {
          scaledSize: new google.maps.Size(26, 44),
          url: '/assets/img/pin-home-special.png'
        }
      }))
    );

    this.store.pipe(select(fromMapSelectors.selectCurrentType)).subscribe((selectedMapTypeId) => {
      this.selectedMapTypeId = selectedMapTypeId;
      this.updateMapType();
    });
  }

  async ionViewWillEnter() {
    await this.mapsService.enableWatchPosition();
  }

  async ionViewWillLeave() {
    await this.mapsService.disableWatchPosition();
  }

  ngAfterViewInit() {
    this.changeDetectorRef.detectChanges();
  }

  mapInitialized(mapInstance: google.maps.Map) {
    this.mapInstance = mapInstance;
    this.centerOnBounds();
    this.updateMapType();
  }

  updateMapType() {
    if (!isNull(this.selectedMapTypeId)) {
      this.mapInstance?.setMapTypeId(this.selectedMapTypeId);
    }
  }

  centerOnBounds() {
    if (!isNull(this.mapInstance) && !isNull(this.schoolsBounds) && !this.schoolsBounds.isEmpty()) {
      this.mapInstance.fitBounds(this.schoolsBounds);
    }
  }

  schoolTrackBy(index: number) {
    return index;
  }

  protected readonly addressType = AddressEnum;
}
